import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, SelectField, FieldWrapper } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { rolesApi, usersApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { User, UserStatus } from '@/types/domain';
import { formatDateTime, orDash } from '@/utils/format';
import { isApiError } from '@/utils/ApiError';

/*
 * One schema with a conditional password: it is required when creating an
 * account and absent when editing, because changing a password is a separate
 * operation with its own endpoint, its own audit action and its own rules.
 */
const schema = z.object({
  email: z.string().min(1, 'E-mail address is required').email('Enter a valid e-mail address'),
  password: z.string(),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  phone: z.string().max(32),
  status: z.enum(['ACTIVE', 'INACTIVE', 'LOCKED', 'PENDING']),
  roleIds: z.array(z.string().uuid()).min(1, 'Assign at least one role'),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  status: 'ACTIVE',
  roleIds: [],
};

export function UsersPage() {
  const { can, user: currentUser } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('lastName,asc');

  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [editing, setEditing] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const [deactivating, setDeactivating] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | undefined>(undefined);
  const [mismatch, setMismatch] = useState(false);

  const params = { ...table.params, ...(statusFilter !== '' ? { status: statusFilter } : {}) };

  const query = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
  });

  const roles = useQuery({
    queryKey: queryKeys.roles.all,
    queryFn: rolesApi.list,
    enabled: can(PERMISSIONS.ROLE_VIEW),
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const phone = values.phone === '' ? undefined : values.phone;
      if (editing === null) {
        if (values.password.length < 10) {
          // Surfaced through the same channel as a server rejection so the
          // message lands on the field rather than in a banner.
          return Promise.reject(
            Object.assign(new Error('Password must be at least 10 characters'), { field: 'password' }),
          );
        }
        return usersApi.create({
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          phone,
          roleIds: values.roleIds,
        });
      }
      return usersApi.update(editing.id, {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone,
        status: values.status,
        roleIds: values.roleIds,
      });
    },
    onSuccess: () => {
      toast.success(editing === null ? 'User created' : 'User updated');
      close();
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    onError: (error) => {
      if (error instanceof Error && 'field' in error) {
        form.setError('password', { type: 'manual', message: error.message });
        return;
      }
      handleApiError(error, form.setError);
    },
  });

  const deactivate = useMutation({
    mutationFn: (user: User) => usersApi.deactivate(user.id),
    onSuccess: () => {
      toast.success('User deactivated and their sessions revoked');
      setDeactivating(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    onError: (error) => {
      handleApiError(error);
      setDeactivating(null);
    },
  });

  /*
   * The strength rules live on the server (@StrongPassword) and are reported
   * verbatim on the field. The only check made here is the one the server
   * cannot make: that the two entries agree.
   */
  const resetPassword = useMutation({
    mutationFn: ({ user, password }: { user: User; password: string }) =>
      usersApi.resetPassword(user.id, password),
    onSuccess: (_, { user }) => {
      toast.success(
        `Temporary password set for ${user.fullName}. They must change it at their next sign-in.`,
      );
      closeReset();
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    onError: (error) => {
      if (isApiError(error) && error.isValidation) {
        const fieldError = error.fieldErrors.find((candidate) => candidate.field === 'temporaryPassword');
        setResetError(fieldError?.message ?? error.fieldErrors.at(0)?.message ?? error.message);
        return;
      }
      handleApiError(error);
    },
  });

  function openReset(user: User) {
    setResetting(user);
    setTemporaryPassword('');
    setConfirmPassword('');
    setResetError(undefined);
    setMismatch(false);
  }

  function closeReset() {
    setResetting(null);
    setTemporaryPassword('');
    setConfirmPassword('');
    setResetError(undefined);
    setMismatch(false);
  }

  function submitReset() {
    if (resetting === null) {
      return;
    }
    if (temporaryPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setResetError(undefined);
    resetPassword.mutate({ user: resetting, password: temporaryPassword });
  }

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(user: User) {
    setEditing(user);
    form.reset({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone ?? '',
      status: user.status,
      roleIds: user.roles.map((role) => role.id),
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'lastName',
      render: (user) => (
        <div>
          <div className="font-medium text-ink">{user.fullName}</div>
          <div className="text-xs text-muted">{user.email}</div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (user) => (
        <div className="flex flex-wrap gap-1">
          {user.roles.map((role) => (
            <Badge key={role.id} tone={role.system ? 'info' : 'neutral'}>
              {role.name}
            </Badge>
          ))}
        </div>
      ),
    },
    { key: 'phone', header: 'Phone', render: (user) => orDash(user.phone) },
    {
      key: 'lastLogin',
      header: 'Last sign-in',
      sortKey: 'lastLoginAt',
      render: (user) => formatDateTime(user.lastLoginAt),
    },
    { key: 'status', header: 'Status', sortKey: 'status', render: (user) => <StatusBadge status={user.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (user) => (
        <div className="flex justify-end gap-2">
          {can(PERMISSIONS.USER_UPDATE) && (
            <Button size="sm" variant="ghost" onClick={() => openEdit(user)}>
              Edit
            </Button>
          )}
          {can(PERMISSIONS.USER_UPDATE) && (
            <Button size="sm" variant="ghost" onClick={() => openReset(user)}>
              Reset password
            </Button>
          )}
          {/* The server refuses self-deactivation; not offering it avoids a pointless error. */}
          {can(PERMISSIONS.USER_DELETE) && user.id !== currentUser?.id && user.status === 'ACTIVE' && (
            <Button size="sm" variant="ghost" onClick={() => setDeactivating(user)}>
              Deactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const selectedRoles = form.watch('roleIds');
  const portalRoleSelected = (roles.data ?? []).some(
    (role) => selectedRoles.includes(role.id) && (role.code === 'STUDENT' || role.code === 'PARENT'),
  );

  return (
    <>
      <ListPage
        title="Users"
        description="Accounts that can sign in. Permissions come from the roles assigned here."
        action={can(PERMISSIONS.USER_CREATE) ? <Button onClick={openCreate}>New user</Button> : undefined}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search by name or e-mail…' }}
        filters={
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
              { value: 'LOCKED', label: 'Locked' },
              { value: 'PENDING', label: 'Pending' },
            ]}
          />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(user) => user.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No users found"
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'New user' : `Edit ${editing.fullName}`}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="First name"
              required
              error={form.formState.errors.firstName?.message}
              {...form.register('firstName')}
            />
            <TextField
              label="Last name"
              required
              error={form.formState.errors.lastName?.message}
              {...form.register('lastName')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="E-mail"
              type="email"
              required
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />
            <TextField label="Phone" error={form.formState.errors.phone?.message} {...form.register('phone')} />
          </div>

          {editing === null ? (
            <TextField
              label="Initial password"
              type="password"
              required
              autoComplete="new-password"
              hint="At least 10 characters with upper case, lower case and a digit. The user must change it at first sign-in."
              error={form.formState.errors.password?.message}
              {...form.register('password')}
            />
          ) : (
            <SelectField label="Status" {...form.register('status')}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="LOCKED">Locked</option>
              <option value="PENDING">Pending</option>
            </SelectField>
          )}

          <FieldWrapper
            label="Roles"
            htmlFor="roleIds"
            required
            error={form.formState.errors.roleIds?.message}
            hint="Changing roles revokes the user's current sessions so the change takes effect promptly."
          >
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-line p-2">
              {(roles.data ?? []).map((role) => (
                <label key={role.id} className="flex items-start gap-2 px-2 py-1.5 hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"                    value={role.id}
                    checked={selectedRoles.includes(role.id)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...selectedRoles, role.id]
                        : selectedRoles.filter((id) => id !== role.id);
                      form.setValue('roleIds', next, { shouldValidate: true });
                    }}
                  />
                  <span className="text-sm">
                    <span className="font-medium text-ink">{role.name}</span>
                    {role.description !== undefined && (
                      <span className="block text-xs text-muted">{role.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {portalRoleSelected && (
              <p role="note" className="mt-2 rounded-md border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">
                Student and Parent accounts also need linking before their portal shows anything: open the
                student&apos;s or guardian&apos;s profile and use <strong>Portal account</strong> to connect this login.
              </p>
            )}
          </FieldWrapper>
        </form>
      </Modal>

      <Modal
        open={resetting !== null}
        title={resetting === null ? 'Reset password' : `Reset password for ${resetting.fullName}`}
        description="Sets a temporary password. The user must replace it at their next sign-in."
        onClose={closeReset}
        footer={
          <>
            <Button variant="secondary" onClick={closeReset} disabled={resetPassword.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitReset}
              loading={resetPassword.isPending}
              disabled={temporaryPassword === '' || confirmPassword === ''}
            >
              Reset password
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submitReset();
          }}
        >
          <TextField
            id="temporaryPassword"
            label="Temporary password"
            type="password"
            required
            autoComplete="new-password"
            hint="At least 10 characters with upper case, lower case and a digit."
            value={temporaryPassword}
            error={resetError}
            onChange={(event) => {
              setTemporaryPassword(event.target.value);
              setResetError(undefined);
              setMismatch(false);
            }}
          />
          <TextField
            id="confirmTemporaryPassword"
            label="Confirm temporary password"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            error={mismatch ? 'The two passwords do not match' : undefined}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setMismatch(false);
            }}
          />
          {/* Lets Enter submit from either field. */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </Modal>

      <ConfirmDialog
        open={deactivating !== null}
        title="Deactivate user"
        message={
          deactivating === null
            ? ''
            : `Deactivate ${deactivating.fullName}? They will be signed out immediately and cannot sign in again. The record is kept because audit events reference it.`
        }
        confirmLabel="Deactivate"
        destructive
        loading={deactivate.isPending}
        onConfirm={() => deactivating !== null && deactivate.mutate(deactivating)}
        onCancel={() => setDeactivating(null)}
      />
    </>
  );
}
